/**
 * @fileoverview Demo Data
 * @task B.INV.2 - Demo data for investor presentations
 *
 * Contains mock data for all financial modules including:
 * - Bills (Accounts Payable)
 * - Invoices (Accounts Receivable)
 * - Vendors
 * - Customers
 * - GL Accounts
 */

// ============================================================================
// Types
// ============================================================================

export type BillStatus = 'draft' | 'pending' | 'approved' | 'paid' | 'overdue';

export interface BillLineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  glAccountId: string;
  glAccountName: string;
}

export interface Bill {
  id: string;
  billNumber: string;
  vendorId: string;
  vendorName: string;
  billDate: string;
  dueDate: string;
  amount: number;
  status: BillStatus;
  lineItems: BillLineItem[];
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Vendor {
  id: string;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  taxId?: string;
  paymentTerms: number | string; // days or terms like "Net 30"
  openBalance?: number;
  totalPurchases?: number;
  status?: 'active' | 'inactive';
}

export interface GLAccount {
  id: string;
  code: string;
  name: string;
  type: 'asset' | 'liability' | 'equity' | 'revenue' | 'expense';
  category: string;
}

// ============================================================================
// Accounts Receivable Types
// ============================================================================

export type InvoiceStatus = 'draft' | 'sent' | 'viewed' | 'partial' | 'paid' | 'overdue' | 'void';

export interface InvoiceLineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  glAccountId?: string;
  glAccountName?: string;
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  customerId: string;
  customerName: string;
  issueDate: string;
  dueDate: string;
  amount: number;
  paidAmount: number;
  status: InvoiceStatus;
  lineItems: InvoiceLineItem[];
  notes?: string;
  discount: number;
  taxRate: number;
  createdAt: string;
  updatedAt: string;
}

export interface Customer {
  id: string;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  taxId?: string;
  paymentTerms: number | string; // days or terms like "Net 30"
  creditLimit?: number;
  openBalance?: number;
  totalRevenue?: number;
  status?: 'active' | 'inactive' | 'on-hold';
}

// ============================================================================
// Demo Data
// ============================================================================

export const demoVendors: Vendor[] = [
  // Cloud & Technology Vendors
  {
    id: 'vendor-001',
    name: 'Amazon Web Services',
    email: 'invoices@aws.amazon.com',
    phone: '+1 (206) 266-4064',
    address: '410 Terry Ave N, Seattle, WA 98109',
    taxId: '91-1646860',
    paymentTerms: 30,
  },
  {
    id: 'vendor-002',
    name: 'Google Cloud Platform',
    email: 'billing@cloud.google.com',
    phone: '+1 (650) 253-0000',
    address: '1600 Amphitheatre Parkway, Mountain View, CA 94043',
    taxId: '77-0493581',
    paymentTerms: 30,
  },
  {
    id: 'vendor-003',
    name: 'Microsoft Azure',
    email: 'invoices@microsoft.com',
    phone: '+1 (425) 882-8080',
    address: 'One Microsoft Way, Redmond, WA 98052',
    taxId: '91-1144442',
    paymentTerms: 30,
  },
  {
    id: 'vendor-004',
    name: 'Slack Technologies',
    email: 'billing@slack.com',
    phone: '+1 (415) 630-7943',
    address: '500 Howard Street, San Francisco, CA 94105',
    taxId: '46-4689498',
    paymentTerms: 30,
  },
  {
    id: 'vendor-005',
    name: 'Atlassian Corporation',
    email: 'invoices@atlassian.com',
    phone: '+1 (415) 701-1110',
    address: '350 Bush Street, San Francisco, CA 94104',
    taxId: '20-3842608',
    paymentTerms: 30,
  },
  // Office Supplies
  {
    id: 'vendor-006',
    name: 'Staples Business Advantage',
    email: 'accounts@staples.com',
    phone: '+1 (800) 333-3330',
    address: '500 Staples Drive, Framingham, MA 01702',
    taxId: '04-2896127',
    paymentTerms: 15,
  },
  {
    id: 'vendor-007',
    name: 'Office Depot Business',
    email: 'business@officedepot.com',
    phone: '+1 (800) 463-3768',
    address: '6600 North Military Trail, Boca Raton, FL 33496',
    taxId: '59-1663954',
    paymentTerms: 15,
  },
  // Utilities
  {
    id: 'vendor-008',
    name: 'Pacific Gas & Electric',
    email: 'businessaccounts@pge.com',
    phone: '+1 (800) 743-5000',
    address: '77 Beale Street, San Francisco, CA 94105',
    taxId: '94-0742640',
    paymentTerms: 21,
  },
  {
    id: 'vendor-009',
    name: 'Comcast Business',
    email: 'businessbilling@comcast.com',
    phone: '+1 (800) 391-3000',
    address: 'One Comcast Center, Philadelphia, PA 19103',
    taxId: '27-0000798',
    paymentTerms: 30,
  },
  {
    id: 'vendor-010',
    name: 'AT&T Business',
    email: 'businessservices@att.com',
    phone: '+1 (800) 321-2000',
    address: '208 S Akard Street, Dallas, TX 75202',
    taxId: '43-1301883',
    paymentTerms: 30,
  },
  // Professional Services & Contractors
  {
    id: 'vendor-011',
    name: 'Deloitte Consulting LLP',
    email: 'invoicing@deloitte.com',
    phone: '+1 (212) 489-1600',
    address: '30 Rockefeller Plaza, New York, NY 10112',
    taxId: '13-2728168',
    paymentTerms: 45,
  },
  {
    id: 'vendor-012',
    name: 'Baker McKenzie LLP',
    email: 'billing@bakermckenzie.com',
    phone: '+1 (312) 861-8000',
    address: '300 East Randolph Street, Chicago, IL 60601',
    taxId: '36-3102694',
    paymentTerms: 30,
  },
  {
    id: 'vendor-013',
    name: 'KPMG Advisory Services',
    email: 'usaccounts@kpmg.com',
    phone: '+1 (212) 758-9700',
    address: '345 Park Avenue, New York, NY 10154',
    taxId: '13-5565207',
    paymentTerms: 45,
  },
  // Hardware & Equipment
  {
    id: 'vendor-014',
    name: 'Dell Technologies',
    email: 'businessorders@dell.com',
    phone: '+1 (800) 915-3355',
    address: 'One Dell Way, Round Rock, TX 78682',
    taxId: '74-2487834',
    paymentTerms: 30,
  },
  {
    id: 'vendor-015',
    name: 'HP Inc.',
    email: 'businessaccounts@hp.com',
    phone: '+1 (800) 474-6836',
    address: '1501 Page Mill Road, Palo Alto, CA 94304',
    taxId: '47-2564708',
    paymentTerms: 30,
  },
  // Marketing & Advertising
  {
    id: 'vendor-016',
    name: 'HubSpot Inc.',
    email: 'billing@hubspot.com',
    phone: '+1 (888) 482-7768',
    address: '25 First Street, Cambridge, MA 02141',
    taxId: '26-0244655',
    paymentTerms: 30,
  },
  {
    id: 'vendor-017',
    name: 'Mailchimp by Intuit',
    email: 'billing@mailchimp.com',
    phone: '+1 (678) 999-0141',
    address: '675 Ponce de Leon Ave NE, Atlanta, GA 30308',
    taxId: '58-2440962',
    paymentTerms: 30,
  },
  // Facilities & Insurance
  {
    id: 'vendor-018',
    name: 'CBRE Group Inc.',
    email: 'invoicing@cbre.com',
    phone: '+1 (213) 613-3333',
    address: '400 S Hope Street, Los Angeles, CA 90071',
    taxId: '95-2820093',
    paymentTerms: 30,
  },
];

export const demoGLAccounts: GLAccount[] = [
  // Expense Accounts
  { id: 'gl-5100', code: '5100', name: 'Software & Subscriptions', type: 'expense', category: 'Operating Expenses' },
  { id: 'gl-5200', code: '5200', name: 'Office Supplies', type: 'expense', category: 'Operating Expenses' },
  { id: 'gl-5300', code: '5300', name: 'Professional Services', type: 'expense', category: 'Operating Expenses' },
  { id: 'gl-5400', code: '5400', name: 'Marketing & Advertising', type: 'expense', category: 'Operating Expenses' },
  { id: 'gl-5500', code: '5500', name: 'Legal & Accounting', type: 'expense', category: 'Operating Expenses' },
  { id: 'gl-5600', code: '5600', name: 'Cloud Infrastructure', type: 'expense', category: 'Operating Expenses' },
  { id: 'gl-5700', code: '5700', name: 'Travel & Entertainment', type: 'expense', category: 'Operating Expenses' },
  { id: 'gl-5800', code: '5800', name: 'Utilities', type: 'expense', category: 'Operating Expenses' },
  { id: 'gl-5900', code: '5900', name: 'Consulting Fees', type: 'expense', category: 'Operating Expenses' },
  // Asset Accounts
  { id: 'gl-1100', code: '1100', name: 'Cash - Operating', type: 'asset', category: 'Current Assets' },
  { id: 'gl-1200', code: '1200', name: 'Accounts Receivable', type: 'asset', category: 'Current Assets' },
  { id: 'gl-1500', code: '1500', name: 'Prepaid Expenses', type: 'asset', category: 'Current Assets' },
  // Liability Accounts
  { id: 'gl-2100', code: '2100', name: 'Accounts Payable', type: 'liability', category: 'Current Liabilities' },
  { id: 'gl-2200', code: '2200', name: 'Accrued Expenses', type: 'liability', category: 'Current Liabilities' },
];

// Demo Customers (AR) - B2B Customers with realistic names and credit limits
export const demoCustomers: Customer[] = [
  // Enterprise & Technology
  {
    id: 'customer-001',
    name: 'Stark Industries',
    email: 'accounts@starkindustries.com',
    phone: '+1 (212) 555-4000',
    address: '200 Park Avenue, New York, NY 10166',
    taxId: '13-4567891',
    paymentTerms: 45,
    creditLimit: 500000,
  },
  {
    id: 'customer-002',
    name: 'Wayne Enterprises',
    email: 'finance@wayneenterprises.com',
    phone: '+1 (312) 555-1000',
    address: '1007 Mountain Drive, Gotham, NJ 08854',
    taxId: '22-3456789',
    paymentTerms: 30,
    creditLimit: 750000,
  },
  {
    id: 'customer-003',
    name: 'Oscorp Industries',
    email: 'ap@oscorp.com',
    phone: '+1 (646) 555-2000',
    address: '725 5th Avenue, New York, NY 10022',
    taxId: '11-2345678',
    paymentTerms: 30,
    creditLimit: 400000,
  },
  // Healthcare & Life Sciences
  {
    id: 'customer-004',
    name: 'Meridian Health Systems',
    email: 'billing@meridianhealth.org',
    phone: '+1 (617) 555-3000',
    address: '100 Healthcare Plaza, Boston, MA 02118',
    taxId: '04-5678901',
    paymentTerms: 60,
    creditLimit: 350000,
  },
  {
    id: 'customer-005',
    name: 'BioGenesis Pharmaceuticals',
    email: 'accounts@biogenesis.com',
    phone: '+1 (858) 555-4000',
    address: '4545 Towne Centre Court, San Diego, CA 92121',
    taxId: '95-6789012',
    paymentTerms: 45,
    creditLimit: 600000,
  },
  // Financial Services
  {
    id: 'customer-006',
    name: 'Vantage Financial Group',
    email: 'invoices@vantagefinancial.com',
    phone: '+1 (415) 555-5000',
    address: '555 California Street, San Francisco, CA 94104',
    taxId: '94-7890123',
    paymentTerms: 30,
    creditLimit: 500000,
  },
  {
    id: 'customer-007',
    name: 'Atlas Capital Partners',
    email: 'payments@atlascapital.com',
    phone: '+1 (212) 555-6000',
    address: '299 Park Avenue, New York, NY 10171',
    taxId: '13-8901234',
    paymentTerms: 30,
    creditLimit: 450000,
  },
  // Manufacturing & Industrial
  {
    id: 'customer-008',
    name: 'Precision Manufacturing Co.',
    email: 'ap@precisionmfg.com',
    phone: '+1 (313) 555-7000',
    address: '2500 Industrial Parkway, Detroit, MI 48207',
    taxId: '38-9012345',
    paymentTerms: 30,
    creditLimit: 275000,
  },
  {
    id: 'customer-009',
    name: 'Summit Energy Partners',
    email: 'invoicing@summitenergy.com',
    phone: '+1 (713) 555-8000',
    address: '1600 Smith Street, Houston, TX 77002',
    taxId: '76-0123456',
    paymentTerms: 45,
    creditLimit: 800000,
  },
  // Retail & Consumer
  {
    id: 'customer-010',
    name: 'Heritage Retail Group',
    email: 'accounts@heritageretail.com',
    phone: '+1 (206) 555-9000',
    address: '411 1st Avenue S, Seattle, WA 98104',
    taxId: '91-1234567',
    paymentTerms: 30,
    creditLimit: 225000,
  },
  {
    id: 'customer-011',
    name: 'Metropolitan Markets Inc.',
    email: 'billing@metrmarkets.com',
    phone: '+1 (310) 555-1100',
    address: '10000 Santa Monica Blvd, Los Angeles, CA 90067',
    taxId: '95-2345678',
    paymentTerms: 15,
    creditLimit: 150000,
  },
  // Professional Services
  {
    id: 'customer-012',
    name: 'Pinnacle Consulting Group',
    email: 'finance@pinnacleconsulting.com',
    phone: '+1 (404) 555-1200',
    address: '3333 Piedmont Road NE, Atlanta, GA 30305',
    taxId: '58-3456789',
    paymentTerms: 30,
    creditLimit: 200000,
  },
  {
    id: 'customer-013',
    name: 'Horizon Legal Services',
    email: 'accounts@horizonlegal.com',
    phone: '+1 (202) 555-1300',
    address: '1301 K Street NW, Washington, DC 20005',
    taxId: '52-4567890',
    paymentTerms: 30,
    creditLimit: 175000,
  },
  // Technology & Software
  {
    id: 'customer-014',
    name: 'DataStream Analytics',
    email: 'invoices@datastreamanalytics.com',
    phone: '+1 (512) 555-1400',
    address: '500 W 2nd Street, Austin, TX 78701',
    taxId: '74-5678901',
    paymentTerms: 30,
    creditLimit: 300000,
  },
  {
    id: 'customer-015',
    name: 'CloudNine Solutions',
    email: 'ap@cloudninesolutions.io',
    phone: '+1 (650) 555-1500',
    address: '100 Hamilton Avenue, Palo Alto, CA 94301',
    taxId: '77-6789012',
    paymentTerms: 30,
    creditLimit: 250000,
  },
  // Real Estate & Construction
  {
    id: 'customer-016',
    name: 'Beacon Properties Group',
    email: 'accounting@beaconproperties.com',
    phone: '+1 (305) 555-1600',
    address: '1111 Brickell Avenue, Miami, FL 33131',
    taxId: '59-7890123',
    paymentTerms: 45,
    creditLimit: 400000,
  },
  {
    id: 'customer-017',
    name: 'Continental Builders LLC',
    email: 'billing@continentalbuilders.com',
    phone: '+1 (602) 555-1700',
    address: '2901 N Central Avenue, Phoenix, AZ 85012',
    taxId: '86-8901234',
    paymentTerms: 30,
    creditLimit: 350000,
  },
  // Logistics & Transportation
  {
    id: 'customer-018',
    name: 'TransGlobal Logistics',
    email: 'invoices@transgloballogistics.com',
    phone: '+1 (973) 555-1800',
    address: '90 Woodbridge Center Drive, Woodbridge, NJ 07095',
    taxId: '22-9012345',
    paymentTerms: 30,
    creditLimit: 280000,
  },
];

// Helper to get date strings
function getDateString(daysOffset: number): string {
  const date = new Date();
  date.setDate(date.getDate() + daysOffset);
  return date.toISOString().split('T')[0] ?? '';
}

export const demoBills: Bill[] = [
  // Cloud & Technology Bills
  {
    id: 'bill-001',
    billNumber: 'BILL-2026-0001',
    vendorId: 'vendor-001',
    vendorName: 'Amazon Web Services',
    billDate: getDateString(-15),
    dueDate: getDateString(15),
    amount: 17850.00,
    status: 'pending',
    lineItems: [
      { id: 'li-001-1', description: 'EC2 Compute - January 2026', quantity: 1, unitPrice: 8500.00, amount: 8500.00, glAccountId: 'gl-5600', glAccountName: 'Cloud Infrastructure' },
      { id: 'li-001-2', description: 'RDS Database Services', quantity: 1, unitPrice: 4200.00, amount: 4200.00, glAccountId: 'gl-5600', glAccountName: 'Cloud Infrastructure' },
      { id: 'li-001-3', description: 'S3 Storage & Transfer', quantity: 1, unitPrice: 3150.00, amount: 3150.00, glAccountId: 'gl-5600', glAccountName: 'Cloud Infrastructure' },
      { id: 'li-001-4', description: 'AWS Support - Business', quantity: 1, unitPrice: 2000.00, amount: 2000.00, glAccountId: 'gl-5600', glAccountName: 'Cloud Infrastructure' },
    ],
    notes: 'Monthly AWS invoice - January 2026',
    createdAt: getDateString(-15),
    updatedAt: getDateString(-15),
  },
  {
    id: 'bill-002',
    billNumber: 'BILL-2026-0002',
    vendorId: 'vendor-002',
    vendorName: 'Google Cloud Platform',
    billDate: getDateString(-18),
    dueDate: getDateString(12),
    amount: 12340.00,
    status: 'approved',
    lineItems: [
      { id: 'li-002-1', description: 'BigQuery Analytics', quantity: 1, unitPrice: 5200.00, amount: 5200.00, glAccountId: 'gl-5600', glAccountName: 'Cloud Infrastructure' },
      { id: 'li-002-2', description: 'Cloud Run Services', quantity: 1, unitPrice: 3800.00, amount: 3800.00, glAccountId: 'gl-5600', glAccountName: 'Cloud Infrastructure' },
      { id: 'li-002-3', description: 'Cloud Storage', quantity: 1, unitPrice: 2340.00, amount: 2340.00, glAccountId: 'gl-5600', glAccountName: 'Cloud Infrastructure' },
      { id: 'li-002-4', description: 'Support Plan', quantity: 1, unitPrice: 1000.00, amount: 1000.00, glAccountId: 'gl-5600', glAccountName: 'Cloud Infrastructure' },
    ],
    createdAt: getDateString(-18),
    updatedAt: getDateString(-5),
  },
  {
    id: 'bill-003',
    billNumber: 'BILL-2026-0003',
    vendorId: 'vendor-003',
    vendorName: 'Microsoft Azure',
    billDate: getDateString(-60),
    dueDate: getDateString(-30),
    amount: 8950.00,
    status: 'paid',
    lineItems: [
      { id: 'li-003-1', description: 'Azure Virtual Machines', quantity: 1, unitPrice: 4500.00, amount: 4500.00, glAccountId: 'gl-5600', glAccountName: 'Cloud Infrastructure' },
      { id: 'li-003-2', description: 'Azure SQL Database', quantity: 1, unitPrice: 2850.00, amount: 2850.00, glAccountId: 'gl-5600', glAccountName: 'Cloud Infrastructure' },
      { id: 'li-003-3', description: 'Azure Active Directory', quantity: 1, unitPrice: 1600.00, amount: 1600.00, glAccountId: 'gl-5100', glAccountName: 'Software & Subscriptions' },
    ],
    notes: 'Paid via ACH - December 2025',
    createdAt: getDateString(-60),
    updatedAt: getDateString(-28),
  },
  {
    id: 'bill-004',
    billNumber: 'BILL-2026-0004',
    vendorId: 'vendor-004',
    vendorName: 'Slack Technologies',
    billDate: getDateString(-7),
    dueDate: getDateString(23),
    amount: 22500.00,
    status: 'pending',
    lineItems: [
      { id: 'li-004-1', description: 'Slack Enterprise Grid - Annual', quantity: 1, unitPrice: 18000.00, amount: 18000.00, glAccountId: 'gl-5100', glAccountName: 'Software & Subscriptions' },
      { id: 'li-004-2', description: 'Premium Support', quantity: 1, unitPrice: 4500.00, amount: 4500.00, glAccountId: 'gl-5100', glAccountName: 'Software & Subscriptions' },
    ],
    notes: 'Annual subscription renewal',
    createdAt: getDateString(-7),
    updatedAt: getDateString(-7),
  },
  {
    id: 'bill-005',
    billNumber: 'BILL-2026-0005',
    vendorId: 'vendor-005',
    vendorName: 'Atlassian Corporation',
    billDate: getDateString(-30),
    dueDate: getDateString(-5),
    amount: 8400.00,
    status: 'overdue',
    lineItems: [
      { id: 'li-005-1', description: 'Jira Software Premium - 50 users', quantity: 50, unitPrice: 100.00, amount: 5000.00, glAccountId: 'gl-5100', glAccountName: 'Software & Subscriptions' },
      { id: 'li-005-2', description: 'Confluence Premium - 50 users', quantity: 50, unitPrice: 60.00, amount: 3000.00, glAccountId: 'gl-5100', glAccountName: 'Software & Subscriptions' },
      { id: 'li-005-3', description: 'Premium Support', quantity: 1, unitPrice: 400.00, amount: 400.00, glAccountId: 'gl-5100', glAccountName: 'Software & Subscriptions' },
    ],
    notes: 'OVERDUE - Please process immediately',
    createdAt: getDateString(-30),
    updatedAt: getDateString(-2),
  },
  // Office Supplies Bills
  {
    id: 'bill-006',
    billNumber: 'BILL-2026-0006',
    vendorId: 'vendor-006',
    vendorName: 'Staples Business Advantage',
    billDate: getDateString(-10),
    dueDate: getDateString(5),
    amount: 2847.50,
    status: 'approved',
    lineItems: [
      { id: 'li-006-1', description: 'Copy Paper - Premium (20 cases)', quantity: 20, unitPrice: 65.00, amount: 1300.00, glAccountId: 'gl-5200', glAccountName: 'Office Supplies' },
      { id: 'li-006-2', description: 'Toner Cartridges (HP)', quantity: 8, unitPrice: 125.00, amount: 1000.00, glAccountId: 'gl-5200', glAccountName: 'Office Supplies' },
      { id: 'li-006-3', description: 'Office Chair Mats', quantity: 5, unitPrice: 89.50, amount: 447.50, glAccountId: 'gl-5200', glAccountName: 'Office Supplies' },
      { id: 'li-006-4', description: 'Desk Organizers', quantity: 10, unitPrice: 10.00, amount: 100.00, glAccountId: 'gl-5200', glAccountName: 'Office Supplies' },
    ],
    createdAt: getDateString(-10),
    updatedAt: getDateString(-3),
  },
  {
    id: 'bill-007',
    billNumber: 'BILL-2026-0007',
    vendorId: 'vendor-007',
    vendorName: 'Office Depot Business',
    billDate: getDateString(-45),
    dueDate: getDateString(-30),
    amount: 1567.80,
    status: 'paid',
    lineItems: [
      { id: 'li-007-1', description: 'Ergonomic Keyboards', quantity: 5, unitPrice: 189.00, amount: 945.00, glAccountId: 'gl-5200', glAccountName: 'Office Supplies' },
      { id: 'li-007-2', description: 'Wireless Mouse', quantity: 10, unitPrice: 45.00, amount: 450.00, glAccountId: 'gl-5200', glAccountName: 'Office Supplies' },
      { id: 'li-007-3', description: 'Monitor Stands', quantity: 4, unitPrice: 43.20, amount: 172.80, glAccountId: 'gl-5200', glAccountName: 'Office Supplies' },
    ],
    notes: 'Paid - December 2025',
    createdAt: getDateString(-45),
    updatedAt: getDateString(-28),
  },
  // Utilities Bills
  {
    id: 'bill-008',
    billNumber: 'BILL-2026-0008',
    vendorId: 'vendor-008',
    vendorName: 'Pacific Gas & Electric',
    billDate: getDateString(-5),
    dueDate: getDateString(16),
    amount: 4256.78,
    status: 'pending',
    lineItems: [
      { id: 'li-008-1', description: 'Electric Service - January 2026', quantity: 1, unitPrice: 3456.78, amount: 3456.78, glAccountId: 'gl-5800', glAccountName: 'Utilities' },
      { id: 'li-008-2', description: 'Gas Service - January 2026', quantity: 1, unitPrice: 800.00, amount: 800.00, glAccountId: 'gl-5800', glAccountName: 'Utilities' },
    ],
    notes: 'Monthly utility bill',
    createdAt: getDateString(-5),
    updatedAt: getDateString(-5),
  },
  {
    id: 'bill-009',
    billNumber: 'BILL-2026-0009',
    vendorId: 'vendor-009',
    vendorName: 'Comcast Business',
    billDate: getDateString(-12),
    dueDate: getDateString(18),
    amount: 1899.00,
    status: 'approved',
    lineItems: [
      { id: 'li-009-1', description: 'Business Internet 1Gbps', quantity: 1, unitPrice: 1299.00, amount: 1299.00, glAccountId: 'gl-5800', glAccountName: 'Utilities' },
      { id: 'li-009-2', description: 'Static IP Block', quantity: 1, unitPrice: 400.00, amount: 400.00, glAccountId: 'gl-5800', glAccountName: 'Utilities' },
      { id: 'li-009-3', description: 'Equipment Rental', quantity: 1, unitPrice: 200.00, amount: 200.00, glAccountId: 'gl-5800', glAccountName: 'Utilities' },
    ],
    createdAt: getDateString(-12),
    updatedAt: getDateString(-8),
  },
  {
    id: 'bill-010',
    billNumber: 'BILL-2026-0010',
    vendorId: 'vendor-010',
    vendorName: 'AT&T Business',
    billDate: getDateString(-55),
    dueDate: getDateString(-25),
    amount: 2345.00,
    status: 'paid',
    lineItems: [
      { id: 'li-010-1', description: 'Business Voice Lines (10)', quantity: 10, unitPrice: 145.00, amount: 1450.00, glAccountId: 'gl-5800', glAccountName: 'Utilities' },
      { id: 'li-010-2', description: 'Mobile Plans - Business', quantity: 5, unitPrice: 179.00, amount: 895.00, glAccountId: 'gl-5800', glAccountName: 'Utilities' },
    ],
    notes: 'Paid - December 2025',
    createdAt: getDateString(-55),
    updatedAt: getDateString(-23),
  },
  // Professional Services & Contractors
  {
    id: 'bill-011',
    billNumber: 'BILL-2026-0011',
    vendorId: 'vendor-011',
    vendorName: 'Deloitte Consulting LLP',
    billDate: getDateString(-20),
    dueDate: getDateString(25),
    amount: 47500.00,
    status: 'pending',
    lineItems: [
      { id: 'li-011-1', description: 'Digital Transformation Consulting - Phase 1', quantity: 1, unitPrice: 35000.00, amount: 35000.00, glAccountId: 'gl-5900', glAccountName: 'Consulting Fees' },
      { id: 'li-011-2', description: 'Change Management Workshop', quantity: 1, unitPrice: 12500.00, amount: 12500.00, glAccountId: 'gl-5900', glAccountName: 'Consulting Fees' },
    ],
    notes: 'Q1 2026 consulting engagement',
    createdAt: getDateString(-20),
    updatedAt: getDateString(-20),
  },
  {
    id: 'bill-012',
    billNumber: 'BILL-2026-0012',
    vendorId: 'vendor-012',
    vendorName: 'Baker McKenzie LLP',
    billDate: getDateString(-40),
    dueDate: getDateString(-10),
    amount: 18750.00,
    status: 'paid',
    lineItems: [
      { id: 'li-012-1', description: 'Contract Review & Negotiation', quantity: 50, unitPrice: 275.00, amount: 13750.00, glAccountId: 'gl-5500', glAccountName: 'Legal & Accounting' },
      { id: 'li-012-2', description: 'Corporate Compliance Advisory', quantity: 20, unitPrice: 250.00, amount: 5000.00, glAccountId: 'gl-5500', glAccountName: 'Legal & Accounting' },
    ],
    notes: 'Paid via wire transfer',
    createdAt: getDateString(-40),
    updatedAt: getDateString(-8),
  },
  {
    id: 'bill-013',
    billNumber: 'BILL-2026-0013',
    vendorId: 'vendor-013',
    vendorName: 'KPMG Advisory Services',
    billDate: getDateString(-8),
    dueDate: getDateString(37),
    amount: 32000.00,
    status: 'draft',
    lineItems: [
      { id: 'li-013-1', description: 'SOC 2 Type II Audit - Q1 2026', quantity: 1, unitPrice: 25000.00, amount: 25000.00, glAccountId: 'gl-5500', glAccountName: 'Legal & Accounting' },
      { id: 'li-013-2', description: 'Remediation Advisory', quantity: 1, unitPrice: 7000.00, amount: 7000.00, glAccountId: 'gl-5300', glAccountName: 'Professional Services' },
    ],
    notes: 'Draft - Pending scope finalization',
    createdAt: getDateString(-8),
    updatedAt: getDateString(-8),
  },
  // Hardware & Equipment
  {
    id: 'bill-014',
    billNumber: 'BILL-2026-0014',
    vendorId: 'vendor-014',
    vendorName: 'Dell Technologies',
    billDate: getDateString(-25),
    dueDate: getDateString(5),
    amount: 45750.00,
    status: 'approved',
    lineItems: [
      { id: 'li-014-1', description: 'Dell PowerEdge R750 Servers (2)', quantity: 2, unitPrice: 15000.00, amount: 30000.00, glAccountId: 'gl-5200', glAccountName: 'Office Supplies' },
      { id: 'li-014-2', description: 'Dell Latitude 5540 Laptops (10)', quantity: 10, unitPrice: 1350.00, amount: 13500.00, glAccountId: 'gl-5200', glAccountName: 'Office Supplies' },
      { id: 'li-014-3', description: 'ProSupport Plus 3-Year', quantity: 1, unitPrice: 2250.00, amount: 2250.00, glAccountId: 'gl-5100', glAccountName: 'Software & Subscriptions' },
    ],
    notes: 'Q1 2026 hardware refresh',
    createdAt: getDateString(-25),
    updatedAt: getDateString(-10),
  },
  {
    id: 'bill-015',
    billNumber: 'BILL-2026-0015',
    vendorId: 'vendor-015',
    vendorName: 'HP Inc.',
    billDate: getDateString(-35),
    dueDate: getDateString(-5),
    amount: 8920.00,
    status: 'overdue',
    lineItems: [
      { id: 'li-015-1', description: 'HP LaserJet Enterprise (2)', quantity: 2, unitPrice: 2800.00, amount: 5600.00, glAccountId: 'gl-5200', glAccountName: 'Office Supplies' },
      { id: 'li-015-2', description: 'HP 27" Monitors (8)', quantity: 8, unitPrice: 415.00, amount: 3320.00, glAccountId: 'gl-5200', glAccountName: 'Office Supplies' },
    ],
    notes: 'OVERDUE - Follow up required',
    createdAt: getDateString(-35),
    updatedAt: getDateString(-3),
  },
  // Marketing & Advertising
  {
    id: 'bill-016',
    billNumber: 'BILL-2026-0016',
    vendorId: 'vendor-016',
    vendorName: 'HubSpot Inc.',
    billDate: getDateString(-15),
    dueDate: getDateString(15),
    amount: 14400.00,
    status: 'pending',
    lineItems: [
      { id: 'li-016-1', description: 'Marketing Hub Professional - Annual', quantity: 1, unitPrice: 10800.00, amount: 10800.00, glAccountId: 'gl-5400', glAccountName: 'Marketing & Advertising' },
      { id: 'li-016-2', description: 'Sales Hub Starter - Annual', quantity: 1, unitPrice: 3600.00, amount: 3600.00, glAccountId: 'gl-5100', glAccountName: 'Software & Subscriptions' },
    ],
    notes: 'Annual subscription - Marketing automation',
    createdAt: getDateString(-15),
    updatedAt: getDateString(-15),
  },
  {
    id: 'bill-017',
    billNumber: 'BILL-2026-0017',
    vendorId: 'vendor-017',
    vendorName: 'Mailchimp by Intuit',
    billDate: getDateString(-50),
    dueDate: getDateString(-20),
    amount: 3588.00,
    status: 'paid',
    lineItems: [
      { id: 'li-017-1', description: 'Mailchimp Premium - Annual', quantity: 1, unitPrice: 3588.00, amount: 3588.00, glAccountId: 'gl-5400', glAccountName: 'Marketing & Advertising' },
    ],
    notes: 'Paid - Email marketing platform',
    createdAt: getDateString(-50),
    updatedAt: getDateString(-18),
  },
  // Facilities & Insurance
  {
    id: 'bill-018',
    billNumber: 'BILL-2026-0018',
    vendorId: 'vendor-018',
    vendorName: 'CBRE Group Inc.',
    billDate: getDateString(-3),
    dueDate: getDateString(27),
    amount: 28500.00,
    status: 'pending',
    lineItems: [
      { id: 'li-018-1', description: 'Office Lease - February 2026', quantity: 1, unitPrice: 25000.00, amount: 25000.00, glAccountId: 'gl-5800', glAccountName: 'Utilities' },
      { id: 'li-018-2', description: 'Common Area Maintenance', quantity: 1, unitPrice: 2500.00, amount: 2500.00, glAccountId: 'gl-5800', glAccountName: 'Utilities' },
      { id: 'li-018-3', description: 'Parking (20 spaces)', quantity: 20, unitPrice: 50.00, amount: 1000.00, glAccountId: 'gl-5800', glAccountName: 'Utilities' },
    ],
    notes: 'Monthly lease payment',
    createdAt: getDateString(-3),
    updatedAt: getDateString(-3),
  },
  // Additional AWS bills for historical data
  {
    id: 'bill-019',
    billNumber: 'BILL-2026-0019',
    vendorId: 'vendor-001',
    vendorName: 'Amazon Web Services',
    billDate: getDateString(-45),
    dueDate: getDateString(-15),
    amount: 16200.00,
    status: 'paid',
    lineItems: [
      { id: 'li-019-1', description: 'EC2 Compute - December 2025', quantity: 1, unitPrice: 7800.00, amount: 7800.00, glAccountId: 'gl-5600', glAccountName: 'Cloud Infrastructure' },
      { id: 'li-019-2', description: 'RDS Database Services', quantity: 1, unitPrice: 4200.00, amount: 4200.00, glAccountId: 'gl-5600', glAccountName: 'Cloud Infrastructure' },
      { id: 'li-019-3', description: 'S3 Storage & Transfer', quantity: 1, unitPrice: 2200.00, amount: 2200.00, glAccountId: 'gl-5600', glAccountName: 'Cloud Infrastructure' },
      { id: 'li-019-4', description: 'AWS Support - Business', quantity: 1, unitPrice: 2000.00, amount: 2000.00, glAccountId: 'gl-5600', glAccountName: 'Cloud Infrastructure' },
    ],
    notes: 'Paid - December 2025 AWS usage',
    createdAt: getDateString(-45),
    updatedAt: getDateString(-12),
  },
  {
    id: 'bill-020',
    billNumber: 'BILL-2026-0020',
    vendorId: 'vendor-001',
    vendorName: 'Amazon Web Services',
    billDate: getDateString(-75),
    dueDate: getDateString(-45),
    amount: 15450.00,
    status: 'paid',
    lineItems: [
      { id: 'li-020-1', description: 'EC2 Compute - November 2025', quantity: 1, unitPrice: 7500.00, amount: 7500.00, glAccountId: 'gl-5600', glAccountName: 'Cloud Infrastructure' },
      { id: 'li-020-2', description: 'RDS Database Services', quantity: 1, unitPrice: 3950.00, amount: 3950.00, glAccountId: 'gl-5600', glAccountName: 'Cloud Infrastructure' },
      { id: 'li-020-3', description: 'S3 Storage & Transfer', quantity: 1, unitPrice: 2000.00, amount: 2000.00, glAccountId: 'gl-5600', glAccountName: 'Cloud Infrastructure' },
      { id: 'li-020-4', description: 'AWS Support - Business', quantity: 1, unitPrice: 2000.00, amount: 2000.00, glAccountId: 'gl-5600', glAccountName: 'Cloud Infrastructure' },
    ],
    notes: 'Paid - November 2025 AWS usage',
    createdAt: getDateString(-75),
    updatedAt: getDateString(-43),
  },
  // More varied bills
  {
    id: 'bill-021',
    billNumber: 'BILL-2026-0021',
    vendorId: 'vendor-002',
    vendorName: 'Google Cloud Platform',
    billDate: getDateString(-48),
    dueDate: getDateString(-18),
    amount: 11500.00,
    status: 'paid',
    lineItems: [
      { id: 'li-021-1', description: 'BigQuery Analytics - December', quantity: 1, unitPrice: 4800.00, amount: 4800.00, glAccountId: 'gl-5600', glAccountName: 'Cloud Infrastructure' },
      { id: 'li-021-2', description: 'Cloud Run Services', quantity: 1, unitPrice: 3500.00, amount: 3500.00, glAccountId: 'gl-5600', glAccountName: 'Cloud Infrastructure' },
      { id: 'li-021-3', description: 'Cloud Storage', quantity: 1, unitPrice: 2200.00, amount: 2200.00, glAccountId: 'gl-5600', glAccountName: 'Cloud Infrastructure' },
      { id: 'li-021-4', description: 'Support Plan', quantity: 1, unitPrice: 1000.00, amount: 1000.00, glAccountId: 'gl-5600', glAccountName: 'Cloud Infrastructure' },
    ],
    notes: 'Paid - December 2025',
    createdAt: getDateString(-48),
    updatedAt: getDateString(-16),
  },
  {
    id: 'bill-022',
    billNumber: 'BILL-2026-0022',
    vendorId: 'vendor-006',
    vendorName: 'Staples Business Advantage',
    billDate: getDateString(-38),
    dueDate: getDateString(-23),
    amount: 1876.25,
    status: 'paid',
    lineItems: [
      { id: 'li-022-1', description: 'Copy Paper - Premium (15 cases)', quantity: 15, unitPrice: 65.00, amount: 975.00, glAccountId: 'gl-5200', glAccountName: 'Office Supplies' },
      { id: 'li-022-2', description: 'Whiteboard Markers (bulk)', quantity: 20, unitPrice: 25.00, amount: 500.00, glAccountId: 'gl-5200', glAccountName: 'Office Supplies' },
      { id: 'li-022-3', description: 'Post-it Notes - Assorted', quantity: 25, unitPrice: 16.05, amount: 401.25, glAccountId: 'gl-5200', glAccountName: 'Office Supplies' },
    ],
    notes: 'Paid - Office supplies restock',
    createdAt: getDateString(-38),
    updatedAt: getDateString(-21),
  },
  {
    id: 'bill-023',
    billNumber: 'BILL-2026-0023',
    vendorId: 'vendor-008',
    vendorName: 'Pacific Gas & Electric',
    billDate: getDateString(-35),
    dueDate: getDateString(-14),
    amount: 4012.45,
    status: 'paid',
    lineItems: [
      { id: 'li-023-1', description: 'Electric Service - December 2025', quantity: 1, unitPrice: 3312.45, amount: 3312.45, glAccountId: 'gl-5800', glAccountName: 'Utilities' },
      { id: 'li-023-2', description: 'Gas Service - December 2025', quantity: 1, unitPrice: 700.00, amount: 700.00, glAccountId: 'gl-5800', glAccountName: 'Utilities' },
    ],
    notes: 'Paid - December utility bill',
    createdAt: getDateString(-35),
    updatedAt: getDateString(-12),
  },
  {
    id: 'bill-024',
    billNumber: 'BILL-2026-0024',
    vendorId: 'vendor-011',
    vendorName: 'Deloitte Consulting LLP',
    billDate: getDateString(-65),
    dueDate: getDateString(-20),
    amount: 35000.00,
    status: 'paid',
    lineItems: [
      { id: 'li-024-1', description: 'Strategic Assessment - Q4 2025', quantity: 1, unitPrice: 35000.00, amount: 35000.00, glAccountId: 'gl-5900', glAccountName: 'Consulting Fees' },
    ],
    notes: 'Paid - Q4 2025 engagement',
    createdAt: getDateString(-65),
    updatedAt: getDateString(-18),
  },
  {
    id: 'bill-025',
    billNumber: 'BILL-2026-0025',
    vendorId: 'vendor-018',
    vendorName: 'CBRE Group Inc.',
    billDate: getDateString(-33),
    dueDate: getDateString(-3),
    amount: 28500.00,
    status: 'paid',
    lineItems: [
      { id: 'li-025-1', description: 'Office Lease - January 2026', quantity: 1, unitPrice: 25000.00, amount: 25000.00, glAccountId: 'gl-5800', glAccountName: 'Utilities' },
      { id: 'li-025-2', description: 'Common Area Maintenance', quantity: 1, unitPrice: 2500.00, amount: 2500.00, glAccountId: 'gl-5800', glAccountName: 'Utilities' },
      { id: 'li-025-3', description: 'Parking (20 spaces)', quantity: 20, unitPrice: 50.00, amount: 1000.00, glAccountId: 'gl-5800', glAccountName: 'Utilities' },
    ],
    notes: 'Paid - January lease',
    createdAt: getDateString(-33),
    updatedAt: getDateString(-1),
  },
  // Additional pending/draft bills for variety
  {
    id: 'bill-026',
    billNumber: 'BILL-2026-0026',
    vendorId: 'vendor-009',
    vendorName: 'Comcast Business',
    billDate: getDateString(-42),
    dueDate: getDateString(-12),
    amount: 1899.00,
    status: 'paid',
    lineItems: [
      { id: 'li-026-1', description: 'Business Internet 1Gbps', quantity: 1, unitPrice: 1299.00, amount: 1299.00, glAccountId: 'gl-5800', glAccountName: 'Utilities' },
      { id: 'li-026-2', description: 'Static IP Block', quantity: 1, unitPrice: 400.00, amount: 400.00, glAccountId: 'gl-5800', glAccountName: 'Utilities' },
      { id: 'li-026-3', description: 'Equipment Rental', quantity: 1, unitPrice: 200.00, amount: 200.00, glAccountId: 'gl-5800', glAccountName: 'Utilities' },
    ],
    notes: 'Paid - December 2025',
    createdAt: getDateString(-42),
    updatedAt: getDateString(-10),
  },
  {
    id: 'bill-027',
    billNumber: 'BILL-2026-0027',
    vendorId: 'vendor-014',
    vendorName: 'Dell Technologies',
    billDate: getDateString(-2),
    dueDate: getDateString(28),
    amount: 5400.00,
    status: 'draft',
    lineItems: [
      { id: 'li-027-1', description: 'Dell Latitude 5540 Laptops (4)', quantity: 4, unitPrice: 1350.00, amount: 5400.00, glAccountId: 'gl-5200', glAccountName: 'Office Supplies' },
    ],
    notes: 'Draft - New hire equipment request',
    createdAt: getDateString(-2),
    updatedAt: getDateString(-2),
  },
  {
    id: 'bill-028',
    billNumber: 'BILL-2026-0028',
    vendorId: 'vendor-004',
    vendorName: 'Slack Technologies',
    billDate: getDateString(-37),
    dueDate: getDateString(-7),
    amount: 1875.00,
    status: 'paid',
    lineItems: [
      { id: 'li-028-1', description: 'Additional User Licenses (25)', quantity: 25, unitPrice: 75.00, amount: 1875.00, glAccountId: 'gl-5100', glAccountName: 'Software & Subscriptions' },
    ],
    notes: 'Paid - User expansion',
    createdAt: getDateString(-37),
    updatedAt: getDateString(-5),
  },
  {
    id: 'bill-029',
    billNumber: 'BILL-2026-0029',
    vendorId: 'vendor-012',
    vendorName: 'Baker McKenzie LLP',
    billDate: getDateString(-5),
    dueDate: getDateString(25),
    amount: 8500.00,
    status: 'pending',
    lineItems: [
      { id: 'li-029-1', description: 'Employment Contract Review', quantity: 20, unitPrice: 275.00, amount: 5500.00, glAccountId: 'gl-5500', glAccountName: 'Legal & Accounting' },
      { id: 'li-029-2', description: 'IP Advisory', quantity: 12, unitPrice: 250.00, amount: 3000.00, glAccountId: 'gl-5500', glAccountName: 'Legal & Accounting' },
    ],
    notes: 'January 2026 legal services',
    createdAt: getDateString(-5),
    updatedAt: getDateString(-5),
  },
  {
    id: 'bill-030',
    billNumber: 'BILL-2026-0030',
    vendorId: 'vendor-003',
    vendorName: 'Microsoft Azure',
    billDate: getDateString(-15),
    dueDate: getDateString(15),
    amount: 9250.00,
    status: 'approved',
    lineItems: [
      { id: 'li-030-1', description: 'Azure Virtual Machines - January', quantity: 1, unitPrice: 4650.00, amount: 4650.00, glAccountId: 'gl-5600', glAccountName: 'Cloud Infrastructure' },
      { id: 'li-030-2', description: 'Azure SQL Database', quantity: 1, unitPrice: 2900.00, amount: 2900.00, glAccountId: 'gl-5600', glAccountName: 'Cloud Infrastructure' },
      { id: 'li-030-3', description: 'Azure Active Directory Premium', quantity: 1, unitPrice: 1700.00, amount: 1700.00, glAccountId: 'gl-5100', glAccountName: 'Software & Subscriptions' },
    ],
    notes: 'Approved - January 2026',
    createdAt: getDateString(-15),
    updatedAt: getDateString(-8),
  },
  // Add 5 more for variety
  {
    id: 'bill-031',
    billNumber: 'BILL-2026-0031',
    vendorId: 'vendor-010',
    vendorName: 'AT&T Business',
    billDate: getDateString(-25),
    dueDate: getDateString(5),
    amount: 2345.00,
    status: 'approved',
    lineItems: [
      { id: 'li-031-1', description: 'Business Voice Lines (10)', quantity: 10, unitPrice: 145.00, amount: 1450.00, glAccountId: 'gl-5800', glAccountName: 'Utilities' },
      { id: 'li-031-2', description: 'Mobile Plans - Business', quantity: 5, unitPrice: 179.00, amount: 895.00, glAccountId: 'gl-5800', glAccountName: 'Utilities' },
    ],
    notes: 'January 2026 telecom',
    createdAt: getDateString(-25),
    updatedAt: getDateString(-15),
  },
  {
    id: 'bill-032',
    billNumber: 'BILL-2026-0032',
    vendorId: 'vendor-007',
    vendorName: 'Office Depot Business',
    billDate: getDateString(-8),
    dueDate: getDateString(7),
    amount: 2234.50,
    status: 'pending',
    lineItems: [
      { id: 'li-032-1', description: 'Standing Desks (3)', quantity: 3, unitPrice: 599.00, amount: 1797.00, glAccountId: 'gl-5200', glAccountName: 'Office Supplies' },
      { id: 'li-032-2', description: 'Office Chairs (2)', quantity: 2, unitPrice: 218.75, amount: 437.50, glAccountId: 'gl-5200', glAccountName: 'Office Supplies' },
    ],
    notes: 'New hire workspace setup',
    createdAt: getDateString(-8),
    updatedAt: getDateString(-8),
  },
  {
    id: 'bill-033',
    billNumber: 'BILL-2026-0033',
    vendorId: 'vendor-005',
    vendorName: 'Atlassian Corporation',
    billDate: getDateString(-60),
    dueDate: getDateString(-30),
    amount: 8400.00,
    status: 'paid',
    lineItems: [
      { id: 'li-033-1', description: 'Jira Software Premium - 50 users', quantity: 50, unitPrice: 100.00, amount: 5000.00, glAccountId: 'gl-5100', glAccountName: 'Software & Subscriptions' },
      { id: 'li-033-2', description: 'Confluence Premium - 50 users', quantity: 50, unitPrice: 60.00, amount: 3000.00, glAccountId: 'gl-5100', glAccountName: 'Software & Subscriptions' },
      { id: 'li-033-3', description: 'Premium Support', quantity: 1, unitPrice: 400.00, amount: 400.00, glAccountId: 'gl-5100', glAccountName: 'Software & Subscriptions' },
    ],
    notes: 'Paid - Q4 2025',
    createdAt: getDateString(-60),
    updatedAt: getDateString(-28),
  },
  {
    id: 'bill-034',
    billNumber: 'BILL-2026-0034',
    vendorId: 'vendor-016',
    vendorName: 'HubSpot Inc.',
    billDate: getDateString(-45),
    dueDate: getDateString(-15),
    amount: 2700.00,
    status: 'paid',
    lineItems: [
      { id: 'li-034-1', description: 'CRM Professional Add-on', quantity: 1, unitPrice: 2700.00, amount: 2700.00, glAccountId: 'gl-5100', glAccountName: 'Software & Subscriptions' },
    ],
    notes: 'Paid - Q4 2025 add-on',
    createdAt: getDateString(-45),
    updatedAt: getDateString(-13),
  },
  {
    id: 'bill-035',
    billNumber: 'BILL-2026-0035',
    vendorId: 'vendor-015',
    vendorName: 'HP Inc.',
    billDate: getDateString(-4),
    dueDate: getDateString(26),
    amount: 3320.00,
    status: 'draft',
    lineItems: [
      { id: 'li-035-1', description: 'HP 27" Monitors (8)', quantity: 8, unitPrice: 415.00, amount: 3320.00, glAccountId: 'gl-5200', glAccountName: 'Office Supplies' },
    ],
    notes: 'Draft - Conference room upgrade',
    createdAt: getDateString(-4),
    updatedAt: getDateString(-4),
  },
];

// ============================================================================
// Demo API Service
// ============================================================================

export interface BillsFilter {
  status?: BillStatus | 'all' | undefined;
  vendorId?: string | undefined;
  search?: string | undefined;
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
}

export interface BillsSummary {
  totalOutstanding: number;
  dueThisWeek: number;
  overdue: number;
  paidThisMonth: number;
  overdueCount: number;
  dueThisWeekCount: number;
}

export interface PaginatedResult<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Demo API Service for Bills
 * Simulates backend API calls with local data
 */
export const billsApi = {
  /**
   * Get paginated and filtered bills
   */
  async getBills(
    filter: BillsFilter = {},
    page: number = 1,
    pageSize: number = 10,
    sortBy: keyof Bill = 'dueDate',
    sortOrder: 'asc' | 'desc' = 'asc'
  ): Promise<PaginatedResult<Bill>> {
    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 300));

    let filtered = [...demoBills];

    // Apply filters
    if (filter.status && filter.status !== 'all') {
      filtered = filtered.filter((b) => b.status === filter.status);
    }

    if (filter.vendorId) {
      filtered = filtered.filter((b) => b.vendorId === filter.vendorId);
    }

    if (filter.search) {
      const search = filter.search.toLowerCase();
      filtered = filtered.filter(
        (b) =>
          b.billNumber.toLowerCase().includes(search) ||
          b.vendorName.toLowerCase().includes(search) ||
          b.lineItems.some((li) => li.description.toLowerCase().includes(search))
      );
    }

    if (filter.dateFrom) {
      filtered = filtered.filter((b) => b.dueDate >= filter.dateFrom!);
    }

    if (filter.dateTo) {
      filtered = filtered.filter((b) => b.dueDate <= filter.dateTo!);
    }

    // Sort
    filtered.sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return 0;
    });

    // Paginate
    const total = filtered.length;
    const totalPages = Math.ceil(total / pageSize);
    const start = (page - 1) * pageSize;
    const data = filtered.slice(start, start + pageSize);

    return {
      data,
      total,
      page,
      pageSize,
      totalPages,
    };
  },

  /**
   * Get bill by ID
   */
  async getBillById(id: string): Promise<Bill | null> {
    await new Promise((resolve) => setTimeout(resolve, 200));
    return demoBills.find((b) => b.id === id) ?? null;
  },

  /**
   * Get bills summary for dashboard
   */
  async getBillsSummary(): Promise<BillsSummary> {
    await new Promise((resolve) => setTimeout(resolve, 200));

    const today = new Date();
    const weekFromNow = new Date();
    weekFromNow.setDate(today.getDate() + 7);

    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

    const todayStr = today.toISOString().split('T')[0] ?? '';
    const weekStr = weekFromNow.toISOString().split('T')[0] ?? '';
    const monthStr = monthStart.toISOString().split('T')[0] ?? '';

    const outstandingBills = demoBills.filter(
      (b) => b.status !== 'paid' && b.status !== 'draft'
    );
    const overdueBills = demoBills.filter((b) => b.status === 'overdue');
    const dueThisWeekBills = outstandingBills.filter(
      (b) => b.dueDate >= todayStr && b.dueDate <= weekStr
    );
    const paidThisMonthBills = demoBills.filter(
      (b) => b.status === 'paid' && b.updatedAt >= monthStr
    );

    return {
      totalOutstanding: outstandingBills.reduce((sum, b) => sum + b.amount, 0),
      dueThisWeek: dueThisWeekBills.reduce((sum, b) => sum + b.amount, 0),
      overdue: overdueBills.reduce((sum, b) => sum + b.amount, 0),
      paidThisMonth: paidThisMonthBills.reduce((sum, b) => sum + b.amount, 0),
      overdueCount: overdueBills.length,
      dueThisWeekCount: dueThisWeekBills.length,
    };
  },

  /**
   * Get all vendors
   */
  async getVendors(): Promise<Vendor[]> {
    await new Promise((resolve) => setTimeout(resolve, 100));
    return [...demoVendors];
  },

  /**
   * Get all GL accounts
   */
  async getGLAccounts(): Promise<GLAccount[]> {
    await new Promise((resolve) => setTimeout(resolve, 100));
    return [...demoGLAccounts];
  },

  /**
   * Update bill status (mock)
   */
  async updateBillStatus(
    id: string,
    status: BillStatus
  ): Promise<Bill | null> {
    await new Promise((resolve) => setTimeout(resolve, 300));
    const bill = demoBills.find((b) => b.id === id);
    if (bill) {
      bill.status = status;
      bill.updatedAt = new Date().toISOString().split('T')[0] ?? '';
      return { ...bill };
    }
    return null;
  },

  /**
   * Generate next bill number
   */
  async getNextBillNumber(): Promise<string> {
    await new Promise((resolve) => setTimeout(resolve, 100));
    const maxNumber = demoBills.reduce((max, b) => {
      const num = parseInt(b.billNumber.split('-')[2] ?? '0', 10);
      return num > max ? num : max;
    }, 0);
    return `BILL-2026-${String(maxNumber + 1).padStart(4, '0')}`;
  },
};

// ============================================================================
// Demo Invoices (Accounts Receivable)
// ============================================================================

export const demoInvoices: Invoice[] = [
  {
    id: 'inv-001',
    invoiceNumber: 'INV-2026-0001',
    customerId: 'customer-001',
    customerName: 'Acme Corporation',
    issueDate: getDateString(-20),
    dueDate: getDateString(10),
    amount: 15750.00,
    paidAmount: 0,
    status: 'sent',
    discount: 0,
    taxRate: 8.25,
    lineItems: [
      {
        id: 'li-inv-001-1',
        description: 'Enterprise Software License - Annual Subscription',
        quantity: 1,
        unitPrice: 12000.00,
        amount: 12000.00,
        glAccountId: 'gl-4100',
        glAccountName: 'Software Revenue',
      },
      {
        id: 'li-inv-001-2',
        description: 'Premium Support Package',
        quantity: 1,
        unitPrice: 2500.00,
        amount: 2500.00,
        glAccountId: 'gl-4200',
        glAccountName: 'Service Revenue',
      },
      {
        id: 'li-inv-001-3',
        description: 'Setup & Configuration Fee',
        quantity: 5,
        unitPrice: 250.00,
        amount: 1250.00,
        glAccountId: 'gl-4200',
        glAccountName: 'Service Revenue',
      },
    ],
    notes: 'Annual license renewal - Thank you for your continued partnership!',
    createdAt: getDateString(-20),
    updatedAt: getDateString(-20),
  },
  {
    id: 'inv-002',
    invoiceNumber: 'INV-2026-0002',
    customerId: 'customer-002',
    customerName: 'Global Enterprises Inc.',
    issueDate: getDateString(-45),
    dueDate: getDateString(-10),
    amount: 48250.00,
    paidAmount: 0,
    status: 'overdue',
    discount: 5,
    taxRate: 8.25,
    lineItems: [
      {
        id: 'li-inv-002-1',
        description: 'Custom Integration Development',
        quantity: 80,
        unitPrice: 200.00,
        amount: 16000.00,
        glAccountId: 'gl-4300',
        glAccountName: 'Consulting Revenue',
      },
      {
        id: 'li-inv-002-2',
        description: 'API Access License - Enterprise Tier',
        quantity: 1,
        unitPrice: 25000.00,
        amount: 25000.00,
        glAccountId: 'gl-4100',
        glAccountName: 'Software Revenue',
      },
      {
        id: 'li-inv-002-3',
        description: 'Training Sessions (8 hours)',
        quantity: 8,
        unitPrice: 500.00,
        amount: 4000.00,
        glAccountId: 'gl-4200',
        glAccountName: 'Service Revenue',
      },
      {
        id: 'li-inv-002-4',
        description: 'Documentation & Knowledge Transfer',
        quantity: 1,
        unitPrice: 3250.00,
        amount: 3250.00,
        glAccountId: 'gl-4200',
        glAccountName: 'Service Revenue',
      },
    ],
    notes: 'URGENT: Payment overdue. Please contact accounts@finova.com',
    createdAt: getDateString(-45),
    updatedAt: getDateString(-5),
  },
  {
    id: 'inv-003',
    invoiceNumber: 'INV-2026-0003',
    customerId: 'customer-003',
    customerName: 'TechStart Solutions',
    issueDate: getDateString(-7),
    dueDate: getDateString(8),
    amount: 4500.00,
    paidAmount: 0,
    status: 'viewed',
    discount: 0,
    taxRate: 8.25,
    lineItems: [
      {
        id: 'li-inv-003-1',
        description: 'Startup License - Monthly Subscription',
        quantity: 3,
        unitPrice: 500.00,
        amount: 1500.00,
        glAccountId: 'gl-4100',
        glAccountName: 'Software Revenue',
      },
      {
        id: 'li-inv-003-2',
        description: 'Additional User Seats (10 users)',
        quantity: 10,
        unitPrice: 50.00,
        amount: 500.00,
        glAccountId: 'gl-4100',
        glAccountName: 'Software Revenue',
      },
      {
        id: 'li-inv-003-3',
        description: 'Onboarding Assistance',
        quantity: 1,
        unitPrice: 2500.00,
        amount: 2500.00,
        glAccountId: 'gl-4200',
        glAccountName: 'Service Revenue',
      },
    ],
    createdAt: getDateString(-7),
    updatedAt: getDateString(-1),
  },
  {
    id: 'inv-004',
    invoiceNumber: 'INV-2026-0004',
    customerId: 'customer-004',
    customerName: 'Metro Healthcare Group',
    issueDate: getDateString(-60),
    dueDate: getDateString(-30),
    amount: 125000.00,
    paidAmount: 125000.00,
    status: 'paid',
    discount: 10,
    taxRate: 0, // Healthcare - tax exempt
    lineItems: [
      {
        id: 'li-inv-004-1',
        description: 'Healthcare Compliance Module - Annual License',
        quantity: 1,
        unitPrice: 75000.00,
        amount: 75000.00,
        glAccountId: 'gl-4100',
        glAccountName: 'Software Revenue',
      },
      {
        id: 'li-inv-004-2',
        description: 'HIPAA Compliance Audit & Certification',
        quantity: 1,
        unitPrice: 35000.00,
        amount: 35000.00,
        glAccountId: 'gl-4300',
        glAccountName: 'Consulting Revenue',
      },
      {
        id: 'li-inv-004-3',
        description: 'Staff Training (40 hours)',
        quantity: 40,
        unitPrice: 375.00,
        amount: 15000.00,
        glAccountId: 'gl-4200',
        glAccountName: 'Service Revenue',
      },
    ],
    notes: 'Paid via wire transfer on ' + getDateString(-28),
    createdAt: getDateString(-60),
    updatedAt: getDateString(-28),
  },
  {
    id: 'inv-005',
    invoiceNumber: 'INV-2026-0005',
    customerId: 'customer-005',
    customerName: 'Pacific Retailers LLC',
    issueDate: getDateString(-3),
    dueDate: getDateString(27),
    amount: 8750.00,
    paidAmount: 0,
    status: 'sent',
    discount: 0,
    taxRate: 9.5,
    lineItems: [
      {
        id: 'li-inv-005-1',
        description: 'Retail Analytics Dashboard',
        quantity: 1,
        unitPrice: 5000.00,
        amount: 5000.00,
        glAccountId: 'gl-4100',
        glAccountName: 'Software Revenue',
      },
      {
        id: 'li-inv-005-2',
        description: 'POS Integration Module',
        quantity: 1,
        unitPrice: 2500.00,
        amount: 2500.00,
        glAccountId: 'gl-4100',
        glAccountName: 'Software Revenue',
      },
      {
        id: 'li-inv-005-3',
        description: 'Data Migration Service',
        quantity: 1,
        unitPrice: 1250.00,
        amount: 1250.00,
        glAccountId: 'gl-4200',
        glAccountName: 'Service Revenue',
      },
    ],
    createdAt: getDateString(-3),
    updatedAt: getDateString(-3),
  },
  {
    id: 'inv-006',
    invoiceNumber: 'INV-2026-0006',
    customerId: 'customer-006',
    customerName: 'Summit Manufacturing Co.',
    issueDate: getDateString(-15),
    dueDate: getDateString(15),
    amount: 32500.00,
    paidAmount: 15000.00,
    status: 'partial',
    discount: 0,
    taxRate: 7.0,
    lineItems: [
      {
        id: 'li-inv-006-1',
        description: 'Manufacturing ERP Module',
        quantity: 1,
        unitPrice: 20000.00,
        amount: 20000.00,
        glAccountId: 'gl-4100',
        glAccountName: 'Software Revenue',
      },
      {
        id: 'li-inv-006-2',
        description: 'Inventory Management Add-on',
        quantity: 1,
        unitPrice: 7500.00,
        amount: 7500.00,
        glAccountId: 'gl-4100',
        glAccountName: 'Software Revenue',
      },
      {
        id: 'li-inv-006-3',
        description: 'Implementation Support (20 hours)',
        quantity: 20,
        unitPrice: 250.00,
        amount: 5000.00,
        glAccountId: 'gl-4200',
        glAccountName: 'Service Revenue',
      },
    ],
    notes: 'Partial payment of $15,000 received on ' + getDateString(-5),
    createdAt: getDateString(-15),
    updatedAt: getDateString(-5),
  },
  {
    id: 'inv-007',
    invoiceNumber: 'INV-2026-0007',
    customerId: 'customer-007',
    customerName: 'Coastal Properties Inc.',
    issueDate: getDateString(-2),
    dueDate: getDateString(43),
    amount: 18500.00,
    paidAmount: 0,
    status: 'draft',
    discount: 0,
    taxRate: 7.5,
    lineItems: [
      {
        id: 'li-inv-007-1',
        description: 'Property Management System - Annual License',
        quantity: 1,
        unitPrice: 12000.00,
        amount: 12000.00,
        glAccountId: 'gl-4100',
        glAccountName: 'Software Revenue',
      },
      {
        id: 'li-inv-007-2',
        description: 'Tenant Portal Module',
        quantity: 1,
        unitPrice: 4500.00,
        amount: 4500.00,
        glAccountId: 'gl-4100',
        glAccountName: 'Software Revenue',
      },
      {
        id: 'li-inv-007-3',
        description: 'Custom Report Development',
        quantity: 8,
        unitPrice: 250.00,
        amount: 2000.00,
        glAccountId: 'gl-4300',
        glAccountName: 'Consulting Revenue',
      },
    ],
    notes: 'Draft - awaiting customer approval',
    createdAt: getDateString(-2),
    updatedAt: getDateString(-2),
  },
  {
    id: 'inv-008',
    invoiceNumber: 'INV-2026-0008',
    customerId: 'customer-008',
    customerName: 'Midwest Logistics Corp.',
    issueDate: getDateString(-40),
    dueDate: getDateString(-10),
    amount: 22750.00,
    paidAmount: 22750.00,
    status: 'paid',
    discount: 2.5,
    taxRate: 6.25,
    lineItems: [
      {
        id: 'li-inv-008-1',
        description: 'Fleet Management System',
        quantity: 1,
        unitPrice: 15000.00,
        amount: 15000.00,
        glAccountId: 'gl-4100',
        glAccountName: 'Software Revenue',
      },
      {
        id: 'li-inv-008-2',
        description: 'GPS Tracking Integration',
        quantity: 1,
        unitPrice: 5000.00,
        amount: 5000.00,
        glAccountId: 'gl-4100',
        glAccountName: 'Software Revenue',
      },
      {
        id: 'li-inv-008-3',
        description: 'Driver Training (12 hours)',
        quantity: 12,
        unitPrice: 200.00,
        amount: 2400.00,
        glAccountId: 'gl-4200',
        glAccountName: 'Service Revenue',
      },
      {
        id: 'li-inv-008-4',
        description: 'Annual Maintenance Contract',
        quantity: 1,
        unitPrice: 350.00,
        amount: 350.00,
        glAccountId: 'gl-4200',
        glAccountName: 'Service Revenue',
      },
    ],
    notes: 'Early payment discount applied - Paid via ACH',
    createdAt: getDateString(-40),
    updatedAt: getDateString(-12),
  },
  {
    id: 'inv-009',
    invoiceNumber: 'INV-2026-0009',
    customerId: 'customer-001',
    customerName: 'Acme Corporation',
    issueDate: getDateString(-25),
    dueDate: getDateString(-5),
    amount: 6500.00,
    paidAmount: 6500.00,
    status: 'paid',
    discount: 0,
    taxRate: 8.25,
    lineItems: [
      {
        id: 'li-inv-009-1',
        description: 'Additional User Licenses (25 seats)',
        quantity: 25,
        unitPrice: 200.00,
        amount: 5000.00,
        glAccountId: 'gl-4100',
        glAccountName: 'Software Revenue',
      },
      {
        id: 'li-inv-009-2',
        description: 'Priority Support Upgrade',
        quantity: 1,
        unitPrice: 1500.00,
        amount: 1500.00,
        glAccountId: 'gl-4200',
        glAccountName: 'Service Revenue',
      },
    ],
    createdAt: getDateString(-25),
    updatedAt: getDateString(-3),
  },
  {
    id: 'inv-010',
    invoiceNumber: 'INV-2026-0010',
    customerId: 'customer-002',
    customerName: 'Global Enterprises Inc.',
    issueDate: getDateString(-5),
    dueDate: getDateString(40),
    amount: 67500.00,
    paidAmount: 0,
    status: 'sent',
    discount: 0,
    taxRate: 8.25,
    lineItems: [
      {
        id: 'li-inv-010-1',
        description: 'Enterprise Platform Upgrade',
        quantity: 1,
        unitPrice: 45000.00,
        amount: 45000.00,
        glAccountId: 'gl-4100',
        glAccountName: 'Software Revenue',
      },
      {
        id: 'li-inv-010-2',
        description: 'Data Analytics Module',
        quantity: 1,
        unitPrice: 15000.00,
        amount: 15000.00,
        glAccountId: 'gl-4100',
        glAccountName: 'Software Revenue',
      },
      {
        id: 'li-inv-010-3',
        description: 'Migration & Testing Services',
        quantity: 30,
        unitPrice: 250.00,
        amount: 7500.00,
        glAccountId: 'gl-4200',
        glAccountName: 'Service Revenue',
      },
    ],
    notes: 'Quarterly platform upgrade',
    createdAt: getDateString(-5),
    updatedAt: getDateString(-5),
  },
];

// ============================================================================
// Invoices API Service
// ============================================================================

export interface InvoicesFilter {
  status?: InvoiceStatus | 'all' | undefined;
  customerId?: string | undefined;
  search?: string | undefined;
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
}

export interface InvoicesSummary {
  totalOutstanding: number;
  expectedThisMonth: number;
  overdue: number;
  collectedThisMonth: number;
  overdueCount: number;
  expectedCount: number;
}

/**
 * Demo API Service for Invoices (Accounts Receivable)
 * Simulates backend API calls with local data
 */
export const invoicesApi = {
  /**
   * Get paginated and filtered invoices
   */
  async getInvoices(
    filter: InvoicesFilter = {},
    page: number = 1,
    pageSize: number = 10,
    sortBy: keyof Invoice = 'dueDate',
    sortOrder: 'asc' | 'desc' = 'asc'
  ): Promise<PaginatedResult<Invoice>> {
    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 300));

    let filtered = [...demoInvoices];

    // Apply filters
    if (filter.status && filter.status !== 'all') {
      filtered = filtered.filter((inv) => inv.status === filter.status);
    }

    if (filter.customerId) {
      filtered = filtered.filter((inv) => inv.customerId === filter.customerId);
    }

    if (filter.search) {
      const search = filter.search.toLowerCase();
      filtered = filtered.filter(
        (inv) =>
          inv.invoiceNumber.toLowerCase().includes(search) ||
          inv.customerName.toLowerCase().includes(search) ||
          inv.lineItems.some((li) => li.description.toLowerCase().includes(search))
      );
    }

    if (filter.dateFrom) {
      filtered = filtered.filter((inv) => inv.dueDate >= filter.dateFrom!);
    }

    if (filter.dateTo) {
      filtered = filtered.filter((inv) => inv.dueDate <= filter.dateTo!);
    }

    // Sort
    filtered.sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return 0;
    });

    // Paginate
    const total = filtered.length;
    const totalPages = Math.ceil(total / pageSize);
    const start = (page - 1) * pageSize;
    const data = filtered.slice(start, start + pageSize);

    return {
      data,
      total,
      page,
      pageSize,
      totalPages,
    };
  },

  /**
   * Get invoice by ID
   */
  async getInvoiceById(id: string): Promise<Invoice | null> {
    await new Promise((resolve) => setTimeout(resolve, 200));
    return demoInvoices.find((inv) => inv.id === id) ?? null;
  },

  /**
   * Get invoices summary for dashboard
   */
  async getInvoicesSummary(): Promise<InvoicesSummary> {
    await new Promise((resolve) => setTimeout(resolve, 200));

    const today = new Date();
    const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

    const todayStr = today.toISOString().split('T')[0] ?? '';
    const monthEndStr = monthEnd.toISOString().split('T')[0] ?? '';
    const monthStr = monthStart.toISOString().split('T')[0] ?? '';

    // Outstanding = all unpaid invoices (not draft, not paid, not void)
    const outstandingInvoices = demoInvoices.filter(
      (inv) => !['draft', 'paid', 'void'].includes(inv.status)
    );

    // Overdue invoices
    const overdueInvoices = demoInvoices.filter((inv) => inv.status === 'overdue');

    // Expected this month = due this month and not yet paid
    const expectedThisMonthInvoices = outstandingInvoices.filter(
      (inv) => inv.dueDate >= todayStr && inv.dueDate <= monthEndStr
    );

    // Collected this month = paid this month
    const collectedThisMonthInvoices = demoInvoices.filter(
      (inv) => inv.status === 'paid' && inv.updatedAt >= monthStr
    );

    return {
      totalOutstanding: outstandingInvoices.reduce((sum, inv) => sum + (inv.amount - inv.paidAmount), 0),
      expectedThisMonth: expectedThisMonthInvoices.reduce((sum, inv) => sum + (inv.amount - inv.paidAmount), 0),
      overdue: overdueInvoices.reduce((sum, inv) => sum + (inv.amount - inv.paidAmount), 0),
      collectedThisMonth: collectedThisMonthInvoices.reduce((sum, inv) => sum + inv.paidAmount, 0),
      overdueCount: overdueInvoices.length,
      expectedCount: expectedThisMonthInvoices.length,
    };
  },

  /**
   * Get all customers
   */
  async getCustomers(): Promise<Customer[]> {
    await new Promise((resolve) => setTimeout(resolve, 100));
    return [...demoCustomers];
  },

  /**
   * Get customer by ID
   */
  async getCustomerById(id: string): Promise<Customer | null> {
    await new Promise((resolve) => setTimeout(resolve, 100));
    return demoCustomers.find((c) => c.id === id) ?? null;
  },

  /**
   * Update invoice status (mock)
   */
  async updateInvoiceStatus(
    id: string,
    status: InvoiceStatus
  ): Promise<Invoice | null> {
    await new Promise((resolve) => setTimeout(resolve, 300));
    const invoice = demoInvoices.find((inv) => inv.id === id);
    if (invoice) {
      invoice.status = status;
      invoice.updatedAt = new Date().toISOString().split('T')[0] ?? '';
      return { ...invoice };
    }
    return null;
  },

  /**
   * Generate next invoice number
   */
  async getNextInvoiceNumber(): Promise<string> {
    await new Promise((resolve) => setTimeout(resolve, 100));
    const maxNumber = demoInvoices.reduce((max, inv) => {
      const num = parseInt(inv.invoiceNumber.split('-')[2] ?? '0', 10);
      return num > max ? num : max;
    }, 0);
    return `INV-2026-${String(maxNumber + 1).padStart(4, '0')}`;
  },

  /**
   * Create invoice (mock)
   */
  async createInvoice(invoiceData: Omit<Invoice, 'id' | 'createdAt' | 'updatedAt'>): Promise<Invoice> {
    await new Promise((resolve) => setTimeout(resolve, 400));
    const newInvoice: Invoice = {
      ...invoiceData,
      id: `inv-${String(demoInvoices.length + 1).padStart(3, '0')}`,
      createdAt: new Date().toISOString().split('T')[0] ?? '',
      updatedAt: new Date().toISOString().split('T')[0] ?? '',
    };
    demoInvoices.push(newInvoice);
    return { ...newInvoice };
  },
};
